import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Automatic cart insurance service.
 *
 * IMPORTANT:
 * Replace the value below with the real variant ID for:
 * "Extra Priority when packing & 100% insurance"
 * https://pureclo.com/products/extra-priority-when-packing-100-insurance-1
 */
const VARIANT_ID_DO_INSURANCE = '49366584361189';
const INSURANCE_HANDLE = 'extra-priority-when-packing-100-insurance-1';
const INSURANCE_SERVICE_PROPERTY = 'priority_insurance';

let isSyncingInsurance = false;
let hasWarnedMissingVariantId = false;
let syncTimeout = null;

const originalFetch = window.fetch.bind(window);

window.fetch = async (input, init) => {
  const response = await originalFetch(input, init);

  if (isCartMutationRequest(input)) {
    response
      .clone()
      .json()
      .catch(() => null)
      .then(() => scheduleInsuranceSync(input, init));
  }

  return response;
};

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (isCheckoutInsurancePreparing()) return;
  if (event.detail?.data?.source === 'cart-insurance-auto-add') return;
  scheduleInsuranceSync();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleInsuranceSync(), { once: true });
} else {
  scheduleInsuranceSync();
}

/**
 * @param {RequestInfo | URL | undefined} input
 */
function isCartMutationRequest(input) {
  if (isCheckoutInsurancePreparing()) return false;
  if (isSyncingInsurance) return false;

  const url = getRequestUrl(input);
  if (!url) return false;

  return /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(url.pathname);
}

/**
 * @param {RequestInfo | URL | undefined} input
 * @param {RequestInit | undefined} init
 */
function scheduleInsuranceSync(input, init) {
  if (isCheckoutInsurancePreparing()) return;

  if (syncTimeout !== null) window.clearTimeout(syncTimeout);
  syncTimeout = window.setTimeout(() => {
    syncTimeout = null;
    syncInsuranceAfterCartMutation(input, init);
  }, 50);
}

/**
 * @param {RequestInfo | URL | undefined} input
 * @param {RequestInit | undefined} init
 */
async function syncInsuranceAfterCartMutation(input, init) {
  if (isCheckoutInsurancePreparing()) return;
  if (isSyncingInsurance) return;

  const variantId = getInsuranceVariantId();
  if (!variantId) return;

  isSyncingInsurance = true;

  try {
    const cart = await fetchCart();
    const normalItems = cart.items.filter((item) => !isInsuranceItem(item));
    const insuranceItems = cart.items.filter(isInsuranceItem);
    const insuranceItem = insuranceItems.find((item) => !isHiddenInsuranceItem(item)) || insuranceItems[0];
    const duplicateInsuranceItems = insuranceItems.filter((item) => item !== insuranceItem);
    const checkoutProtectionVisible = cart.attributes?._checkout_protection_visible === 'true';
    let sections = {};

    if (normalItems.length === 0) {
      for (const item of insuranceItems) {
        const data = await changeInsuranceQuantity(item, 0);
        sections = data.sections || sections;
      }
      if (checkoutProtectionVisible) await setCheckoutProtectionVisible(false);
      if (insuranceItems.length > 0) await dispatchCartRefresh(sections);
      return;
    }

    if (!checkoutProtectionVisible) {
      for (const item of insuranceItems) {
        const data = await changeInsuranceQuantity(item, 0);
        sections = data.sections || sections;
      }
      if (insuranceItems.length > 0) await dispatchCartRefresh(sections);
      return;
    }

    for (const item of duplicateInsuranceItems) {
      const data = await changeInsuranceQuantity(item, 0);
      sections = data.sections || sections;
    }

    if (insuranceItem && insuranceItem.quantity !== 1) {
      const data = await changeInsuranceQuantity(insuranceItem, 1);
      sections = data.sections || sections;
    }

    if (!insuranceItem && checkoutProtectionVisible) {
      await setCheckoutProtectionVisible(false);
    }

    if (duplicateInsuranceItems.length > 0 || (insuranceItem && insuranceItem.quantity !== 1)) {
      await dispatchCartRefresh(sections);
    }
  } catch (error) {
    console.warn('[Cart insurance auto-add]', error);
  } finally {
    isSyncingInsurance = false;
  }
}

function getInsuranceVariantId() {
  const variantId = VARIANT_ID_DO_INSURANCE.trim();
  const isPlaceholder = !variantId || variantId === 'COLOQUE_AQUI_O_ID_DA_VARIANTE';

  if (isPlaceholder) {
    if (!hasWarnedMissingVariantId) {
      console.warn('[Cart insurance auto-add] Replace VARIANT_ID_DO_INSURANCE in assets/cart-insurance-auto-add.js.');
      hasWarnedMissingVariantId = true;
    }
    return '';
  }

  return variantId;
}

function isCheckoutInsurancePreparing() {
  return window.__purecloPreparingCheckoutInsurance === true;
}

async function fetchCart() {
  const response = await originalFetch(`${getRoutesRoot()}cart.js`, { credentials: 'same-origin' });
  return response.json();
}

/**
 * @param {{ key?: string, variant_id?: number }} insuranceItem
 * @param {number} quantity
 */
async function changeInsuranceQuantity(insuranceItem, quantity) {
  const response = await originalFetch(getCartChangeUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: insuranceItem.key || insuranceItem.variant_id,
      quantity,
      sections: getCartItemsSectionIds().join(','),
      sections_url: window.location.pathname,
    }),
  });

  const data = await response.json();
  if (data.status) throw data;

  return data;
}

/**
 * @param {boolean} visible
 */
async function setCheckoutProtectionVisible(visible) {
  const response = await originalFetch(getCartUpdateUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      attributes: {
        _checkout_protection_visible: visible ? 'true' : '',
      },
    }),
  });

  const data = await response.json();
  if (data.status) throw data;

  return data;
}

/**
 * @param {Record<string, string>} sections
 */
async function dispatchCartRefresh(sections) {
  const cart = await fetchCart();

  document.dispatchEvent(
    new CartAddEvent(cart ?? undefined, 'cart-insurance-auto-add', {
      source: 'cart-insurance-auto-add',
      itemCount: 0,
      variantId: getInsuranceVariantId(),
      sections,
    })
  );
}

/**
 * @param {any} item
 */
function isInsuranceItem(item) {
  return (
    item?.variant_id?.toString() === getInsuranceVariantId() ||
    item?.handle === INSURANCE_HANDLE ||
    item?.properties?._auto_added_service === INSURANCE_SERVICE_PROPERTY
  );
}

/**
 * @param {any} item
 */
function isHiddenInsuranceItem(item) {
  return item?.properties?._cart_drawer_hidden === 'true' || item?.properties?._auto_added_service === INSURANCE_SERVICE_PROPERTY;
}

function getCartItemsSectionIds() {
  const ids = new Set();
  const cartItemsComponents = document.querySelectorAll('cart-items-component');

  for (const item of cartItemsComponents) {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.add(item.dataset.sectionId);
    }
  }

  return Array.from(ids);
}

/**
 * @param {RequestInfo | URL | undefined} input
 */
function getRequestUrl(input) {
  try {
    if (typeof input === 'string' || input instanceof URL) return new URL(input, window.location.origin);
    if (input instanceof Request) return new URL(input.url, window.location.origin);
  } catch (_error) {
    return null;
  }

  return null;
}

function getRoutesRoot() {
  return window.Shopify?.routes?.root || '/';
}

function getCartChangeUrl() {
  return Theme.routes.cart_change_url.endsWith('.js') ? Theme.routes.cart_change_url : `${Theme.routes.cart_change_url}.js`;
}

function getCartUpdateUrl() {
  return Theme.routes.cart_update_url.endsWith('.js') ? Theme.routes.cart_update_url : `${Theme.routes.cart_update_url}.js`;
}
